import type { PropsWithChildren, ReactNode } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import { CommentModalShell } from './CommentModalShell';
import { CommentSortControl } from './CommentSortControl';
import type { CommentSortMode } from './comment-threading';

export function CommentThreadModal({
  children,
  closeAccessibilityLabel,
  closeDisabled,
  closeOnBackdropPress,
  commentCount,
  compact,
  composer,
  emptyText,
  emptyTitle = 'No comments yet',
  error,
  errorText = 'Unable to load comments.',
  loading,
  loadingText = 'Loading comments...',
  onChangeSortMode,
  onClose,
  preview,
  sortMode,
  title = 'Comment',
  visible,
}: PropsWithChildren<{
  closeAccessibilityLabel?: string;
  closeDisabled?: boolean;
  closeOnBackdropPress?: boolean;
  commentCount: number;
  compact: boolean;
  composer?: ReactNode;
  emptyText: string;
  emptyTitle?: string;
  error?: Error | null;
  errorText?: string;
  loading?: boolean;
  loadingText?: string;
  onChangeSortMode?: (sortMode: CommentSortMode) => void;
  onClose: () => void;
  preview: ReactNode;
  sortMode?: CommentSortMode;
  title?: string;
  visible: boolean;
}>) {
  const hasError = Boolean(error);
  const summaryCard = (
    <View style={styles.summaryCard}>
      <View style={styles.summaryLeft}>
        <Text style={styles.summaryTitle}>Comments</Text>
        <View
          style={[
            styles.summaryBadge,
            commentCount === 0 ? styles.summaryBadgeEmpty : null,
          ]}
        >
          <Text
            style={[
              styles.summaryBadgeText,
              commentCount === 0 ? styles.summaryBadgeTextEmpty : null,
            ]}
          >
            {commentCount}
          </Text>
        </View>
      </View>
      {sortMode && onChangeSortMode ? (
        <CommentSortControl
          compact={compact}
          value={sortMode}
          onChange={onChangeSortMode}
        />
      ) : null}
    </View>
  );

  return (
    <CommentModalShell
      closeAccessibilityLabel={closeAccessibilityLabel}
      closeDisabled={closeDisabled}
      closeOnBackdropPress={closeOnBackdropPress}
      compact={compact}
      title={title}
      bottomContent={composer ?? null}
      topContent={
        <>
          {preview}
          {summaryCard}
        </>
      }
      visible={visible}
      onClose={onClose}
    >
      <View style={styles.thread}>
        {loading ? <Text style={styles.statusText}>{loadingText}</Text> : null}
        {!loading && hasError ? (
          <Text style={styles.errorText}>{errorText}</Text>
        ) : null}
        {!loading && !hasError && commentCount === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>{emptyTitle}</Text>
            <Text style={styles.emptyText}>{emptyText}</Text>
          </View>
        ) : null}
        {children}
      </View>
    </CommentModalShell>
  );
}

const styles = StyleSheet.create({
  emptyState: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xs,
    padding: spacing.md,
  },
  emptyText: {
    color: colors.secondary,
    fontSize: 13,
    lineHeight: 19,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
  errorText: {
    color: colors.danger,
    fontSize: 13,
    fontWeight: '800',
  },
  statusText: {
    color: colors.secondary,
    fontSize: 13,
    lineHeight: 19,
  },
  summaryBadge: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 999,
    justifyContent: 'center',
    minHeight: 28,
    minWidth: 38,
    paddingHorizontal: spacing.sm,
  },
  summaryBadgeEmpty: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderWidth: 1,
  },
  summaryBadgeText: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '900',
  },
  summaryBadgeTextEmpty: {
    color: colors.secondary,
  },
  summaryCard: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  summaryLeft: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    minWidth: 0,
  },
  summaryTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  thread: {
    gap: spacing.md,
    paddingTop: spacing.sm,
  },
});
